/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * Browser-side error reporting to Sentry, dependency-free. Hooks
 * window.onerror and unhandledrejection, and exposes captureError for
 * explicit reporting (used by the ErrorBoundary).
 *
 * No-op unless REACT_APP_SENTRY_DSN is set at build time. Deliberately
 * minimal: no breadcrumbs, no session replay, no PII — just the error,
 * the page, and the surface (client / mlro / review).
 */
const dsn = process.env.REACT_APP_SENTRY_DSN || '';
const parsed = dsn.match(/^https:\/\/([^@]+)@([^/]+)\/(\d+)$/);

// Distinctive fragment of the officer-facing sentence that api/_anthropic.js
// returns once it has retried an Anthropic overload (HTTP 529) or rate limit
// to exhaustion. Those are transient upstream conditions, not faults in our
// code, and the serverless side has already reported them to Sentry at warning
// level with the endpoint, upstream status and attempt count. Reporting them
// again from the browser as an exception is what put "Error: API 529 on
// /api/chat" in the error stream.
//
// MUST match the wording of OFFICER_MESSAGE in api/_anthropic.js. The two
// cannot share a module because src/ and api/ are separate build targets, so
// if that sentence is reworded, reword this fragment with it.
// Marker phrase the API uses for outcomes that are the upstream's answer
// rather than our defect (a register with no record for a reference, or an
// endpoint a vendor has not confirmed yet). Kept in one place so the API and
// the monitoring wrapper cannot drift apart.
const EXPECTED_UPSTREAM_MARKER = 'The registry has no record';
const UPSTREAM_BUSY_MARKER = 'The AI service is busy';

function surface() {
  const p = window.location.pathname;
  if (p.startsWith('/mlro')) return 'mlro-dashboard';
  if (p.startsWith('/review')) return 'jaybee-review';
  if (p.startsWith('/director-review')) return 'director-review';
  return 'client-onboarding';
}

// Personal-data scrubbing (v1_577): mirrors api/_sentry.js. Response snippets
// and error messages can carry emails, names, and row dumps; strip before send.
const PII_KEYS = /^(email|user_email|contact_email|recipient_email|name|contact_name|company_name|phone|address|dob|passport|wallet|password|token|invite_token|access_token|authorization|apikey|secret|content|body|transcript|collected_data)$/i;
function scrub(str) {
  if (typeof str !== 'string') return str;
  return str
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '[jwt]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\b\d{9,}\b/g, '[number]');
}
function scrubObj(o, depth) {
  depth = depth || 0;
  if (o == null || typeof o !== 'object') return scrub(o);
  if (depth >= 4) return '[nested]';
  if (Array.isArray(o)) return o.slice(0, 20).map(function (v) { return scrubObj(v, depth + 1); });
  var out = {};
  Object.keys(o).forEach(function (k) { out[k] = PII_KEYS.test(k) ? '[redacted]' : scrubObj(o[k], depth + 1); });
  return out;
}

export function captureError(error, context = {}) {
  try {
    if (!parsed) return;
    const [, publicKey, host, projectId] = parsed;
    const err = error instanceof Error ? error : new Error(String(error));

    // ── Noise discipline (06/08/26) ──────────────────────────────────────
    // 1. Visitors' browser extensions wrap window.fetch and sometimes break
    //    it (frame_ant.js et al). A network failure whose stack runs through
    //    an extension frame is the visitor's machine, not our code: drop it.
    // 2. A genuine network failure ("Failed to fetch") from our own frames
    //    is signal only in aggregate: a real outage shows up server-side and
    //    across many users. Report it at warning level so the stream keeps
    //    it without paging anyone per event. An explicit context.level from
    //    the caller always wins.
    const stackStr = String((err && err.stack) || '');
    const extensionWrapped = /(chrome|moz|safari)-extension:\/\//.test(stackStr);
    const networkKind = context.kind === 'network' || /Failed to fetch|NetworkError when attempting|Load failed/i.test(String(err.message || ''));
    if (extensionWrapped && networkKind) return;
    if (networkKind && !context.level) context = { ...context, level: 'warning' };
    const event = {
      event_id: Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join(''),
      timestamp: new Date().toISOString(),
      platform: 'javascript',
      level: context.level || 'error',
      environment: process.env.REACT_APP_SENTRY_ENV || 'production',
      logger: 'compliantid-web',
      tags: { surface: surface() },
      request: { url: window.location.origin + window.location.pathname }, // path only — invite tokens live in the query string and must never be reported
      extra: { stack: scrub((err.stack || '').slice(0, 4000)), ...scrubObj(context.extra || {}) },
      exception: { values: [{ type: err.name || 'Error', value: scrub((err.message || 'Unknown error').slice(0, 500)) }] }
    };
    // sendBeacon survives page unloads; fetch is the fallback
    const url = `https://${host}/api/${projectId}/store/?sentry_version=7&sentry_client=compliantid%2F1.0&sentry_key=${publicKey}`;
    const body = JSON.stringify(event);
    if (navigator.sendBeacon) navigator.sendBeacon(url, body);
    else fetch(url, { method: 'POST', body, keepalive: true }).catch(() => {});
  } catch (_) { /* never throw from monitoring */ }
}

export function initErrorReporting() {
  if (!parsed) return;
  window.addEventListener('error', e => {
    captureError(e.error || e.message || 'window error');
  });
  window.addEventListener('unhandledrejection', e => {
    captureError(e.reason || 'unhandled promise rejection');
  });

  // Report failed calls to our own API. In-function Sentry only sees errors
  // inside code that actually ran; deployment-level failures (missing export,
  // broken import, function timeout — Vercel's FUNCTION_INVOCATION_FAILED)
  // never execute a line of handler code, so the only place they can be
  // caught is here, on the response the browser receives.
  try {
    const origFetch = window.fetch.bind(window);
    const isOwnApi = (url) => {
      if (!url) return false;
      if (url.startsWith('/api/')) return true;
      try { const u = new URL(url, window.location.origin); return u.origin === window.location.origin && u.pathname.startsWith('/api/'); } catch (_) { return false; }
    };
    // Requests cancelled because the user is LEAVING (refresh, navigation,
    // tab close) are not failures worth an alert; track unload so the
    // interceptor can tell "the network failed" from "the user left".
    let unloading = false;
    try {
      window.addEventListener('pagehide', () => { unloading = true; });
      window.addEventListener('beforeunload', () => { unloading = true; });
    } catch (_) {}

    window.fetch = async function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      let res;
      try {
        res = await origFetch(input, init);
      } catch (netErr) {
        // Network-level failure (server unreachable, CORS, aborted deploy).
        // Suppress the noise cases: page unloading, an explicitly aborted
        // request, or a browser that knows it is offline.
        const aborted = netErr && (netErr.name === 'AbortError' || /aborted/i.test(String(netErr.message || '')));
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        if (isOwnApi(url) && !unloading && !aborted && !offline) {
          captureError(new Error(`API network failure on ${url.split('?')[0]}`), {
            extra: { endpoint: url.split('?')[0], kind: 'network', message: String(netErr && netErr.message || netErr).slice(0, 300) }
          });
        }
        throw netErr; // never swallow — the app's own handling must still run
      }
      try {
        if (res && res.status >= 500 && isOwnApi(url)) {
          let snippet = '';
          try { snippet = (await res.clone().text()).slice(0, 400); } catch (_) {}
          if (snippet.includes(EXPECTED_UPSTREAM_MARKER)) {
            // A third-party register simply had no record for the reference,
            // or told us the endpoint does not exist. That is an answer, not a
            // fault of ours: the officer already sees it stated plainly on the
            // panel with the fix route. Console only, so it stays visible in
            // debugging without filling the issue tracker during testing.
            try { console.warn(`[api] expected upstream outcome on ${url.split('?')[0]} (${res.status})`); } catch (_) {}
          } else if (snippet.includes(UPSTREAM_BUSY_MARKER)) {
            // Anthropic was overloaded and the server already retried with
            // backoff and reported the exhaustion itself. Console only, so the
            // condition is still visible while debugging without raising an
            // exception for something that is not our bug.
            try { console.warn(`[api] upstream busy after retries on ${url.split('?')[0]} (${res.status})`); } catch (_) {}
          } else {
            // Path only in the report — invite tokens live in query strings
            captureError(new Error(`API ${res.status} on ${url.split('?')[0]}`), {
              extra: { endpoint: url.split('?')[0], status: res.status, response: snippet }
            });
          }
        }
      } catch (_) { /* monitoring must never affect the request path */ }
      return res;
    };
  } catch (_) { /* if wrapping fails, the app runs exactly as before */ }
}
